import { ref } from 'vue'

/**
 * «È disponibile una nuova versione»: avvisa, senza mai ricaricare da solo.
 *
 * Dopo un rilascio una scheda rimasta aperta continua a girare con il frontend vecchio. Ricaricarla in automatico
 * cancellerebbe ciò che l'utente sta scrivendo (era la causa dei ricaricamenti «senza senso» del 28/10/2026): si mostra un
 * avviso e il ricaricamento lo decide chi sta lavorando.
 *
 * Come si accorge della versione nuova:
 * - in produzione, ogni 5 minuti e quando la scheda torna visibile, rilegge la pagina iniziale e confronta il file
 *   `assets/index-<hash>.js` con quello caricato (il nome cambia a ogni build);
 * - sempre, se un pezzo di codice caricato a richiesta non si trova più (`vite:preloadError`: succede quando il rilascio
 *   ha tolto i vecchi file).
 */
const disponibile = ref(false)
const INTERVALLO_MS = 5 * 60 * 1000
/** Fra un controllo e l'altro passa almeno questo tempo, anche se la scheda torna visibile più volte. */
const PAUSA_MINIMA_MS = 30 * 1000
let avviato = false
let ultimoControllo = 0

/** Il file principale caricato da questa scheda, es. `/assets/index-AbC123.js` (null in sviluppo). */
function scriptCorrente(): string | null {
  const el = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/index-"]')
  return el ? new URL(el.src, document.baseURI).pathname : null
}

function scriptPubblicato(html: string): string | null {
  return /\/assets\/index-[\w-]+\.js/.exec(html)?.[0] ?? null
}

export async function controllaNuovaVersione(): Promise<void> {
  const corrente = scriptCorrente()
  if (!corrente || disponibile.value) return
  if (Date.now() - ultimoControllo < PAUSA_MINIMA_MS) return
  ultimoControllo = Date.now()
  try {
    const res = await fetch(import.meta.env.BASE_URL, { cache: 'no-store', headers: { Accept: 'text/html' } })
    // un redirect (login, proxy) o una pagina di errore non sono la pagina iniziale: non dicono nulla sulla versione
    if (!res.ok || res.redirected || !(res.headers.get('content-type') ?? '').includes('text/html')) return
    const pubblicato = scriptPubblicato(await res.text())
    if (pubblicato && pubblicato !== corrente) disponibile.value = true
  } catch { /* offline o backend in riavvio: si riprova al prossimo giro */ }
}

export function segnalaNuovaVersione(): void {
  disponibile.value = true
}

/** Da chiamare una sola volta al mount di App.vue. */
export function startControlloVersione(): void {
  if (avviato) return
  avviato = true
  window.addEventListener('vite:preloadError', segnalaNuovaVersione)
  if (!import.meta.env.PROD) return
  setInterval(() => void controllaNuovaVersione(), INTERVALLO_MS)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void controllaNuovaVersione()
  })
}

export function useNuovaVersione() {
  return {
    disponibile,
    /** Chiude l'avviso fino al prossimo controllo che trova ancora una versione nuova. */
    rimanda: () => { disponibile.value = false },
    ricarica: () => window.location.reload(),
  }
}
